import { SliceRenderer } from './webgl/sliceRenderer';
import type { Frame } from './types';

// Long edge of the exported image. The renderer stretches the frame texture to
// whatever size we give it, so the output resolution is independent of the
// (possibly letterboxed/clamped) on-screen canvas — we pick a crisp fixed size
// and let `aspect` set the proportions.
const EXPORT_LONG_EDGE = 2000;

/**
 * Render a HU Frame off-screen with the given window/level and return a PNG
 * data URL. `aspect` (width / height) is the on-screen display aspect, so the
 * export matches what the user sees rather than the raw voxel-grid proportions
 * (which ignore non-square spacing) or the visible canvas backing store.
 */
export function frameToPngDataUrl(
    frame: Frame,
    center: number,
    width: number,
    aspect: number
): string | null {
    const a = aspect > 0 && Number.isFinite(aspect) ? aspect : frame.w / frame.h;
    const outW = a >= 1 ? EXPORT_LONG_EDGE : Math.max(1, Math.round(EXPORT_LONG_EDGE * a));
    const outH = a >= 1 ? Math.max(1, Math.round(EXPORT_LONG_EDGE / a)) : EXPORT_LONG_EDGE;

    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    let r: SliceRenderer;
    try {
        r = new SliceRenderer(canvas);
    } catch {
        return null;
    }
    try {
        r.resize(outW, outH);
        r.setFrame(frame.data, frame.w, frame.h);
        r.draw(center, width);
        return canvas.toDataURL('image/png');
    } finally {
        r.dispose();
    }
}
